import React, { useRef, useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom';
import { postFormDataWithProgress, postUserDescriptionAnswer, storeFileURL, deleteFile } from '../../assets/api/userActions';
import { getAnswerFiles, getDisplayFileName, mergeAnswerFilesIntoAttempt } from '../../assets/utils/utils';
import { getQuestionImages, getQuestionVoices } from '../../assets/utils/questionMedia';
import QuestionImageGallery from '../QuestionImageGallery';
import QuestionAudioList from '../QuestionAudioList';

import { ReactComponent as Delete } from '../../assets/icons/Delete.svg';
import { ReactComponent as Eye } from '../../assets/icons/Eye.svg';
import { ReactComponent as Load } from '../../assets/icons/loading.svg';
import { ReactComponent as Upload } from '../../assets/icons/Upload.svg';
import FilePreviewModal from './filePreviewModal';
import RemoteFilePreviewModal from './remoteFilePreviewModal';

import classes from '../../styles/components/modals/uploadModal.module.scss';
import Swal from 'sweetalert2';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import MicRecorder from 'mic-recorder-to-mp3';

const ACCEPTED_FORMATS = 'image/*,application/pdf';
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const ACCEPTED_AUDIO_FORMATS = 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/x-m4a,audio/mp4,audio/aac';
const ACCEPTED_AUDIO_EXTENSIONS = ['mp3', 'mpeg', 'wav', 'ogg', 'webm', 'm4a', 'mp4', 'aac'];

const isAudioFile = (fileOrName) => {
    const name = typeof fileOrName === 'string'
        ? fileOrName
        : (fileOrName?.original_name || fileOrName?.name || fileOrName?.path || '');
    const ext = String(name).split('.').pop()?.toLowerCase() || '';
    return ACCEPTED_AUDIO_EXTENSIONS.includes(ext);
};

const formatRecordTime = (seconds) => {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const makeRecordingFileName = () => {
    const ts = new Date();
    const stamp = `${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, '0')}${String(ts.getDate()).padStart(2, '0')}_${String(ts.getHours()).padStart(2, '0')}${String(ts.getMinutes()).padStart(2, '0')}${String(ts.getSeconds()).padStart(2, '0')}`;
    return `recording_${stamp}.mp3`;
};

/** فقط تایپ مجاز است — paste، drop و میان‌برهای clipboard در ادیتور غیرفعال می‌شوند */
const blockEditorPasteAndDrop = (editor) => {
    const viewDocument = editor.editing.view.document;
    const stop = (evt) => evt.stop();
    viewDocument.on('paste', stop, { priority: 'highest' });
    viewDocument.on('drop', stop, { priority: 'highest' });
    viewDocument.on('clipboardInput', stop, { priority: 'highest' });
    viewDocument.on('keydown', (evt, data) => {
        const domEvent = data.domEvent;
        if (!domEvent) return;
        const isPasteShortcut = (domEvent.ctrlKey || domEvent.metaKey) && domEvent.key?.toLowerCase() === 'v';
        const isCutShortcut = (domEvent.ctrlKey || domEvent.metaKey) && domEvent.key?.toLowerCase() === 'x';
        if (isPasteShortcut || isCutShortcut) {
            domEvent.preventDefault();
            evt.stop();
        }
    }, { priority: 'highest' });
};

const MicIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20} {...props}>
        <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
    </svg>
);

const StopIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={18} height={18} {...props}>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
);

const BackDrop = (props) => {
    const preventDragDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    return (
        <div
            className={classes.modalBackDrop}
            onClick={props.onConfirm}
            onDragOver={preventDragDefault}
            onDrop={preventDragDefault}
        />
    );
};

const SelectedAudioPlayer = ({ file }) => {
    const [src, setSrc] = useState('');
    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setSrc(url);
        return () => {
            try { URL.revokeObjectURL(url); } catch (_) {}
        };
    }, [file]);
    if (!src) return null;
    return (
        <audio controls preload="metadata" src={src} className={classes.answerModal_audioPlayer}>
            مرورگر شما از پخش صدا پشتیبانی نمی‌کند.
        </audio>
    );
};

const OverlayUploadModal = (props) => {
    const viewOnly = Boolean(props.viewOnly);

    const fileInputRef = useRef();
    const audioInputRef = useRef();
    const dragCounterRef = useRef(0);
    const micRecorderRef = useRef(null);
    const recordTimerRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [userAnswer, setUserAnswer] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordSeconds, setRecordSeconds] = useState(0);
    const [recordError, setRecordError] = useState(null);


    const updateButtonName = (fileNames) => {
        const display = Array.isArray(fileNames)
            ? (fileNames.length > 1 ? `${fileNames.length} فایل آپلود شد` : fileNames[0] || 'فایل آپلود شد')
            : fileNames;
        props.uploadFileName(display);
    };

    const addFiles = useCallback((newFiles) => {
        if (!newFiles?.length) return;
        const validFiles = Array.from(newFiles).filter(f => {
            const ext = f.name.split('.').pop()?.toLowerCase();
            return ACCEPTED_EXTENSIONS.includes(ext);
        });
        setFiles(prev => [...prev, ...validFiles]);
    }, []);

    const addAudioFiles = useCallback((newFiles) => {
        if (!newFiles?.length) return;
        const validFiles = Array.from(newFiles).filter(f => {
            const ext = f.name.split('.').pop()?.toLowerCase();
            return ACCEPTED_AUDIO_EXTENSIONS.includes(ext) || (f.type && f.type.startsWith('audio/'));
        });
        if (!validFiles.length) {
            Swal.fire({
                icon: 'warning',
                title: 'فرمت فایل صوتی پشتیبانی نمی‌شود',
                text: 'فقط فایل‌های صوتی mp3 / wav / ogg / m4a / webm پذیرفته می‌شود.',
                confirmButtonText: 'باشه',
            });
            return;
        }
        setFiles(prev => [...prev, ...validFiles]);
    }, []);

    const clearRecordTimer = () => {
        if (recordTimerRef.current) {
            clearInterval(recordTimerRef.current);
            recordTimerRef.current = null;
        }
    };

    const finishRecording = useCallback(async (discard) => {
        const recorder = micRecorderRef.current;
        micRecorderRef.current = null;
        clearRecordTimer();
        setIsRecording(false);
        setRecordSeconds(0);
        if (!recorder) return;
        try {
            recorder.stop();
            if (discard) return;
            const [buffer, blob] = await recorder.getMp3();
            const file = new File(buffer, makeRecordingFileName(), {
                type: blob?.type || 'audio/mpeg',
                lastModified: Date.now(),
            });
            setFiles(prev => [...prev, file]);
        } catch (err) {
            if (!discard) {
                const msg = err?.message === 'No buffer to send'
                    ? 'صدایی ضبط نشد. دوباره تلاش کنید.'
                    : 'ذخیره فایل صوتی ممکن نشد.';
                Swal.fire({ icon: 'error', title: msg, confirmButtonText: 'باشه' });
            }
        }
    }, []);

    const startRecording = useCallback(async () => {
        if (viewOnly || isRecording) return;
        setRecordError(null);
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
            Swal.fire({
                icon: 'error',
                title: 'ضبط صدا در این مرورگر پشتیبانی نمی‌شود',
                confirmButtonText: 'باشه',
            });
            return;
        }
        try {
            const recorder = new MicRecorder({ bitRate: 128 });
            micRecorderRef.current = recorder;
            await recorder.start();
            setIsRecording(true);
            setRecordSeconds(0);
            recordTimerRef.current = setInterval(() => {
                setRecordSeconds(s => s + 1);
            }, 1000);
        } catch (err) {
            micRecorderRef.current = null;
            setIsRecording(false);
            setRecordSeconds(0);
            const msg = err?.name === 'NotAllowedError'
                ? 'دسترسی به میکروفن داده نشد.'
                : 'دسترسی به میکروفن ممکن نیست.';
            setRecordError(msg);
            Swal.fire({ icon: 'error', title: msg, confirmButtonText: 'باشه' });
        }
    }, [isRecording, viewOnly]);

    const stopRecording = useCallback(() => {
        finishRecording(false);
    }, [finishRecording]);

    const cancelRecording = useCallback(() => {
        finishRecording(true);
    }, [finishRecording]);

    useEffect(() => {
        return () => {
            cancelRecording();
        };
    }, [cancelRecording]);

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const showAnswerSaveError = (data) => {
        setLoading(false);
        setUploadProgress({});
        Swal.fire({
            icon: 'error',
            title: data?.message || 'ثبت پاسخ با خطا مواجه شد. لطفاً دوباره تلاش کنید.',
            confirmButtonText: 'باشه',
        });
    };

    const formDataRes = async (uploadResults) => {
        const data = await postUserDescriptionAnswer(props.id, props.attemptID, userAnswer);
        if (!data?.attempt || data?.status !== 'answer-registered') {
            showAnswerSaveError(data);
            return;
        }
        const storedFiles = [];
        let attemptPayload = data.attempt;
        for (const res of uploadResults) {
            const fileURLData = {
                path: res.path,
                original_name: res.original_name,
                answer_id: data.created_answer.id,
                driver: "ftp",
                type: "open",
            };
            const storeRes = await storeFileURL(fileURLData);
            const filePayload = storeRes?.file ?? storeRes;
            storedFiles.push({
                id: filePayload?.id,
                path: filePayload?.path || res.path,
                original_name: filePayload?.original_name || res.original_name,
            });
            if (storeRes?.attempt) {
                attemptPayload = storeRes.attempt;
            }
        }

        const savedAnswer = attemptPayload?.answers?.find((a) => a.question_id === props.id);
        if (savedAnswer?.answer) {
            props.setBodyData(savedAnswer.answer);
        }

        if (storedFiles.length > 0) {
            const hasFiles = savedAnswer ? getAnswerFiles(savedAnswer).length > 0 : false;
            if (!hasFiles) {
                attemptPayload = mergeAnswerFilesIntoAttempt(attemptPayload, props.id, storedFiles);
            }
        }

        setLoading(false);
        props.uploadSuccess();
        if (attemptPayload) {
            const answerAfter = attemptPayload.answers?.find((a) => a.question_id === props.id);
            const filesForQuestion = answerAfter ? getAnswerFiles(answerAfter) : storedFiles;
            setLocalUploadedFiles(filesForQuestion);
            props.onUploadedFilesChange?.(filesForQuestion);
            props.answerAttemptRes(attemptPayload);
        }
        props.onAnswerSaved?.();
        const names = uploadResults.map(r => r.original_name || r.path?.split('/').pop());
        setTimeout(() => updateButtonName(names), 1000);
        props.onConfirm();
    };

    const nullFormDataRes = async () => {
        const data = await postUserDescriptionAnswer(props.id, props.attemptID, userAnswer);
        if (!data?.attempt || data?.status !== 'answer-registered') {
            showAnswerSaveError(data);
            return;
        }
        props.answerAttemptRes(data.attempt);
        for (let el of data.attempt.answers) {
            if (el.question_id === props.id) props.setBodyData(el.answer);
        }
        props.uploadSuccess();
        setLoading(false);
        props.onAnswerSaved?.();
        setTimeout(() => props.uploadFileName("پاسخ تشریحی ثبت شد."), 1000);
        props.onConfirm();
    };

    const onSubmitClick = async (e) => {
        e.preventDefault();
        if (!files.length && !userAnswer) {
            Swal.fire({
                icon: "warning",
                title: "برای ثبت پاسخ، جواب را تایپ یا فایل ارسال کنید",
                confirmButtonText: "باشه"
            });
            return;
        }

        setLoading(true);
        setUploadProgress({});

        if (files.length > 0) {
            const uploadResults = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                setUploadProgress(prev => ({ ...prev, [i]: 0 }));
                const formData = new FormData();
                formData.append("file", file);
                const res = await postFormDataWithProgress(formData, (percent) => {
                    setUploadProgress(prev => ({ ...prev, [i]: percent }));
                });
                if (res?.data) {
                    uploadResults.push({
                        path: res.data.path,
                        original_name: res.data.original_name || file.name,
                    });
                }
                setUploadProgress(prev => ({ ...prev, [i]: 100 }));
            }
            if (uploadResults.length > 0) {
                await formDataRes(uploadResults);
            } else {
                setLoading(false);
                setUploadProgress({});
            }
        } else {
            await nullFormDataRes();
        }
    };

    const answeredHandler = (data, isUserTyped) => {
        if (!isUserTyped) setUserAnswer(data);
    };

    const onFileChangeHandler = (event) => {
        addFiles(event.target.files);
        event.target.value = '';
    };

    const onAudioFileChangeHandler = (event) => {
        addAudioFiles(event.target.files);
        event.target.value = '';
    };

    useEffect(() => {
        props.body && answeredHandler(props.body);
    }, [props.body]);

    /* ضروری برای Chrome: preventDefault در dragover تا drop event فعال شود - فقط preventDefault بدون stopPropagation */
    useEffect(() => {
        const h = (e) => e.preventDefault();
        document.addEventListener('dragover', h, false);
        return () => document.removeEventListener('dragover', h);
    }, []);

    /* هندلرهای درگ‌اند‌دراپ */
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        dragCounterRef.current += 1;
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setIsDragOver(false);
        }
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        if (e.dataTransfer?.files?.length) {
            addFiles(e.dataTransfer.files);
        }
    };
    /* جلوگیری از باز شدن فایل در تب - فقط preventDefault بدون stopPropagation */
    const handleFormDragOver = (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const [previewFile, setPreviewFile] = useState(null);
    const [previewRemoteFile, setPreviewRemoteFile] = useState(null);
    const [localUploadedFiles, setLocalUploadedFiles] = useState(props.uploadedFiles || []);
    const uploadedFiles = localUploadedFiles;
    const questionMediaSource = props.question || {
        image: props.questionImage,
        voice: props.questionVoice,
        images: props.question?.images,
        voices: props.question?.voices,
    };
    const questionImages = getQuestionImages(questionMediaSource);
    const questionVoices = getQuestionVoices(questionMediaSource);
    const hasQuestionHeader = Boolean(
        props.questionBody ||
        questionImages.length ||
        questionVoices.length
    );

    useEffect(() => {
        setLocalUploadedFiles(props.uploadedFiles || []);
    }, [props.uploadedFiles]);

    const handleUploadedFileDelete = async (file) => {
        if (viewOnly) return;
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: 'حذف فایل',
            text: `آیا از حذف "${getDisplayFileName(file)}" اطمینان دارید؟`,
            showCancelButton: true,
            confirmButtonText: 'بله، حذف شود',
            cancelButtonText: 'انصراف',
        });
        if (!confirmed.isConfirmed) return;
        try {
            const res = await deleteFile(file.id);
            if (res) {
                setLocalUploadedFiles(prev => prev.filter(f => f.id !== file.id));
                if (res.attempt) {
                    props.answerAttemptRes?.(res.attempt);
                }
                props.onFileDeleted?.();
                Swal.fire({ icon: 'success', title: 'فایل با موفقیت حذف شد.' });
            }
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'خطا در حذف فایل' });
        }
    };

    /* جلوگیری از باز شدن فایل در تب جدید - هندلر روی فرم و backdrop */
    const preventDropDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    return (
        <form
            className={classes.answerModal}
            onSubmit={(e) => {
                if (viewOnly) {
                    e.preventDefault();
                    return;
                }
                onSubmitClick(e);
            }}
            key={props.id}
            onDragOver={viewOnly ? undefined : handleFormDragOver}
            onDrop={viewOnly ? undefined : preventDropDefault}
        >
            <p className={classes.answerModal_header}>{`پاسخ سوال ${props.quNo} :`}{viewOnly && ' (فقط مشاهده)'}</p>
            {hasQuestionHeader && (
                <div className={classes.answerModal_questionHeader}>
                    <h4 className={classes.answerModal_sectionTitle}>متن سوال</h4>
                    {props.questionBody && (
                        <p className={classes.answerModal_questionBody}>{props.questionBody}</p>
                    )}
                    {(questionImages.length > 0 || questionVoices.length > 0) && (
                        <div className={classes.answerModal_questionAttachments}>
                            <QuestionImageGallery images={questionImages} />
                            <QuestionAudioList
                                voices={questionVoices}
                                idPrefix={`modal-q-${props.id}`}
                            />
                        </div>
                    )}
                </div>
            )}
            {uploadedFiles.length > 0 && (
                <div className={classes.answerModal_uploadedSection}>
                    <h4 className={classes.answerModal_sectionTitle}>فایل‌های آپلود شده</h4>
                    <div className={classes.answerModal_filesGrid}>
                        {uploadedFiles.map((file) => {
                            const audio = isAudioFile(file);
                            const audioUrl = audio
                                ? (typeof file.path === 'string' ? file.path : (file.path?.path || ''))
                                : '';
                            return (
                                <div
                                    key={file.id || file.path}
                                    className={`${classes.answerModal_fileCard} ${audio ? classes.answerModal_fileCardAudio : ''}`}
                                >
                                    {audio ? (
                                        <div className={classes.answerModal_audioCardContent}>
                                            <span className={classes.answerModal_audioCardName} title={getDisplayFileName(file)}>
                                                {getDisplayFileName(file)}
                                            </span>
                                            <audio
                                                controls
                                                preload="metadata"
                                                src={audioUrl}
                                                className={classes.answerModal_audioPlayer}
                                            >
                                                مرورگر شما از پخش صدا پشتیبانی نمی‌کند.
                                            </audio>
                                        </div>
                                    ) : (
                                        <span
                                            className={classes.answerModal_fileName}
                                            onClick={() => setPreviewRemoteFile(file)}
                                            title="مشاهده"
                                        >
                                            {getDisplayFileName(file)}
                                        </span>
                                    )}
                                    <div className={classes.answerModal_fileActions}>
                                        {!audio && (
                                            <button
                                                type="button"
                                                className={classes.answerModal_fileAction}
                                                onClick={() => setPreviewRemoteFile(file)}
                                                title="مشاهده"
                                            >
                                                <Eye width={16} height={16} />
                                            </button>
                                        )}
                                        {file.id && !viewOnly && (
                                            <button
                                                type="button"
                                                className={classes.answerModal_fileAction}
                                                onClick={() => handleUploadedFileDelete(file)}
                                                title="حذف"
                                            >
                                                <Delete width={16} height={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {viewOnly ? (
                <div
                    className={classes.answerModal_questionBody}
                    style={{ minHeight: 80, border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}
                    dangerouslySetInnerHTML={{ __html: userAnswer && String(userAnswer).trim() ? userAnswer : '<p style="color:#6b7280;margin:0">متنی ثبت نشده است.</p>' }}
                />
            ) : (
                <CKEditor
                    editor={ClassicEditor}
                    data={userAnswer ?? ""}
                    config={{
                        language: {
                            ui: "en",
                            content: "ar"
                        }
                    }}
                    onReady={(editor) => blockEditorPasteAndDrop(editor)}
                    onChange={(event, editor) => {
                        setUserAnswer(editor.getData());
                    }}
                />
            )}
            <div className={classes.answerModal_footer}>
                {!viewOnly && (
                <div className={classes.answerModal_footer__uploadBtn}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        id={classes.uploadBtn}
                        accept={ACCEPTED_FORMATS}
                        onChange={onFileChangeHandler}
                        multiple
                    />
                    <label
                        htmlFor={classes.uploadBtn}
                        className={`${classes.answerModal_footer__dropZone} ${isDragOver ? classes.answerModal_footer__dropZoneDragOver : ''}`}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        style={{ cursor: 'pointer', margin: 0 }}
                    >
                        <Upload className={classes.answerModal_footer__dropZoneIcon} />
                        <span className={classes.answerModal_footer__dropZoneText}>انتخاب فایل</span>
                        <span className={classes.answerModal_footer__dropZoneHint}>فرمت‌های مجاز: تصویر، PDF • امکان انتخاب و آپلود چند فایل</span>
                    </label>
                    {files.length > 0 && (
                        <div className={classes.answerModal_footer__selectedFiles}>
                            {files.map((file, index) => {
                                const audio = isAudioFile(file);
                                return (
                                    <div key={index} className={`${classes.selectedFileItem} ${audio ? classes.selectedFileItemAudio : ''}`}>
                                        <div className={classes.fileItemContent}>
                                            {audio ? (
                                                <>
                                                    <span className={classes.fileItemName} title={file.name}>
                                                        {file.name}
                                                    </span>
                                                    <SelectedAudioPlayer file={file} />
                                                </>
                                            ) : (
                                                <span
                                                    className={classes.fileItemName}
                                                    onClick={(e) => { e.preventDefault(); setPreviewFile(file); }}
                                                    title="مشاهده"
                                                >
                                                    {file.name}
                                                </span>
                                            )}
                                            {uploadProgress[index] !== undefined && (
                                                <div className={classes.fileProgressWrapper}>
                                                    <div className={classes.fileProgressBar}>
                                                        <div
                                                            className={classes.fileProgressBarFill}
                                                            style={{ width: `${uploadProgress[index]}%` }}
                                                        />
                                                    </div>
                                                    <span className={classes.fileProgressBarText}>{uploadProgress[index]}%</span>
                                                </div>
                                            )}
                                        </div>
                                        {uploadProgress[index] === undefined && (
                                            <div className={classes.fileItemActions}>
                                                {!audio && (
                                                    <div
                                                        className={classes.fileItemView}
                                                        onClick={(e) => { e.preventDefault(); setPreviewFile(file); }}
                                                        title="مشاهده"
                                                    >
                                                        <Eye width={14} height={14} />
                                                    </div>
                                                )}
                                                <div
                                                    className={classes.fileItemDelete}
                                                    onClick={(e) => { e.preventDefault(); removeFile(index); }}
                                                    title="حذف"
                                                >
                                                    <Delete width={14} height={14} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                )}
                {!viewOnly && (
                    <div className={classes.answerModal_footer__audioBtn}>
                        <input
                            type="file"
                            ref={audioInputRef}
                            id={classes.audioUploadBtn}
                            accept={ACCEPTED_AUDIO_FORMATS}
                            onChange={onAudioFileChangeHandler}
                            multiple
                        />
                        <div className={classes.answerModal_footer__audioActions}>
                            <label
                                htmlFor={classes.audioUploadBtn}
                                className={classes.answerModal_footer__audioUploadZone}
                                style={{ cursor: 'pointer', margin: 0 }}
                            >
                                <Upload className={classes.answerModal_footer__dropZoneIcon} />
                                <div className={classes.answerModal_footer__audioTexts}>
                                    <span className={classes.answerModal_footer__dropZoneText}>آپلود فایل صوتی</span>
                                    <span className={classes.answerModal_footer__dropZoneHint}>فرمت‌های مجاز: mp3 / wav / ogg / m4a</span>
                                </div>
                            </label>
                            {!isRecording ? (
                                <button
                                    type="button"
                                    className={classes.answerModal_footer__recordBtn}
                                    onClick={startRecording}
                                    title="ضبط ویس"
                                >
                                    <span className={classes.answerModal_footer__recordIcon}>
                                        <MicIcon />
                                    </span>
                                    <span className={classes.answerModal_footer__recordLabel}>ضبط ویس</span>
                                </button>
                            ) : (
                                <div className={classes.answerModal_footer__recordingBox}>
                                    <span className={classes.answerModal_footer__recordingDot} />
                                    <span className={classes.answerModal_footer__recordingTime}>{formatRecordTime(recordSeconds)}</span>
                                    <span className={classes.answerModal_footer__recordingHint}>در حال ضبط…</span>
                                    <button
                                        type="button"
                                        className={classes.answerModal_footer__recordStopBtn}
                                        onClick={stopRecording}
                                        title="پایان ضبط و افزودن"
                                    >
                                        <StopIcon />
                                        <span>پایان ضبط</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={classes.answerModal_footer__recordCancelBtn}
                                        onClick={cancelRecording}
                                        title="لغو ضبط"
                                    >
                                        لغو
                                    </button>
                                </div>
                            )}
                        </div>
                        {recordError && (
                            <span className={classes.answerModal_footer__recordError}>{recordError}</span>
                        )}
                    </div>
                )}
                {previewFile && (
                    <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
                )}
                {previewRemoteFile && (
                    <RemoteFilePreviewModal file={previewRemoteFile} onClose={() => setPreviewRemoteFile(null)} />
                )}
                {viewOnly ? (
                    <button type="button" style={{ border: 'none' }} id={classes.confirmBtn} onClick={() => props.onConfirm()}>بستن</button>
                ) : loading ? (
                    <div id={classes.loadingBtn}><Load height={"30"} /></div>
                ) : (
                    <button type="submit" style={{ border: 'none' }} id={classes.confirmBtn}>ثبت پاسخ</button>
                )}
            </div>
        </form>
    );
};

const UploadModal = (props) => {
    return (
        <React.Fragment>
            {ReactDOM.createPortal(<BackDrop onConfirm={props.onConfirm} />, document.getElementById("backdrop-root"))}
            {ReactDOM.createPortal(<OverlayUploadModal id={props.id} uploadSuccess={props.uploadSuccess} answerAttemptRes={props.answerAttemptRes}
                uploadFileName={props.uploadFileName} attemptID={props.attemptID} quNo={props.quNo} clickedOnFunc={props.clickedOnFunc}
                body={props.body} uploadedFileName={props.uploadedFileName} uploadedFiles={props.uploadedFiles}
                setBodyData={props.setBodyData} onFileDeleted={props.onFileDeleted} onAnswerUpdated={props.onAnswerUpdated}
                onAnswerSaved={props.onAnswerSaved}
                onUploadedFilesChange={props.onUploadedFilesChange}
                questionBody={props.questionBody}
                question={props.question}
                questionImage={props.questionImage}
                questionVoice={props.questionVoice}
                viewOnly={props.viewOnly}
                onConfirm={props.onConfirm} />, document.getElementById("overlayUploadModal-root"))}
        </React.Fragment>
    );
};

export default UploadModal;
